'use client'

import { resolvePageReadinessState } from '@brew/observability/page-readiness-state'
import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import { usePaginatedQuery } from 'convex/react'
import { Loader2 } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  useTransition,
} from 'react'
import { preloadEmailJsxPreview } from '@/components/email-preview/email-jsx-preview-dynamic'
import { BlurFade } from '@/components/ui/blur-fade'
import { api } from '@/convex/_generated/api'
import { useInfiniteScrollSentinel } from '@/hooks/use-infinite-scroll-sentinel'
import { usePageContentReadiness } from '@/hooks/use-page-content-readiness'
import { usePublicTemplateSearch } from '@/hooks/use-public-template-search'
import { templateTypeFilter } from '@/lib/templates/category-filter'
import { isReviewedTemplateAlias } from '@/lib/templates/public-canonical'
import { templateDetailHref } from '@/lib/templates/public-path'
import { publicTemplateReferenceOf } from '@/lib/templates/public-reference'
import {
  TEMPLATE_GALLERY_SEARCH_LIMIT,
  USER_COLLECTION_TEMPLATE_TYPES,
} from '@/lib/templates/search-constants'
import { useTemplateSearchQuery } from '@/lib/templates/search-query-state'
import { TEMPLATE_GRID_CLASSES } from '../lib/grid-classes'
import { toTemplateSnapshot } from '../lib/search-results'
import { useTemplatesBasePath } from '../lib/templates-surface-context'
import type { TemplateBrandMetaByDomain, TemplateSnapshot } from '../lib/types'
import { buildTemplatesUrl } from '../lib/url-helpers'
import { ForwardedEmailGrid } from './forwarded-email-grid'
import { type SentDesignsSeed, SentEmailGrid } from './sent-email-grid'
import { EmailPreviewCard } from './template-card'

const FORWARDED_TYPE = 'forwarded'
const SENT_TYPE = 'sent'
const PAGE_SIZE = 30
const subscribeToHydration = () => () => {}
const getHydratedClientSnapshot = () => true
const getHydratedServerSnapshot = () => false

interface EmailGridClientProps {
  initialEmails: Array<TemplateSnapshot>
  currentType?: string
  currentBrand?: string
  currentDesigner?: string
  userId?: string
  /**
   * Ordering seed for the brand-diversified deck (`lib/templates/deck.ts`).
   * MUST be the same value the server rendered `initialEmails` with — the
   * live subscription takes over as source of truth on hydration, so a
   * different seed would visibly reshuffle the grid under the user.
   */
  seed?: string
  /**
   * The Sent collection's server read (`?type=sent`): painted until the
   * grid's subscription answers, for the same org and brand only.
   */
  sentSeed?: SentDesignsSeed | null
  /**
   * Curated brand display meta keyed by domain (name + rehosted logo),
   * built server-side from the cached brand facet list. Live-subscription
   * rows key into it by `snapshot.brandDomain`, so freshly paginated
   * cards resolve identically to the SSR page. Optional: `ForwardedEmailGrid`
   * callers omit it and cards fall back to derived name + hotlink logo.
   */
  brandMeta?: TemplateBrandMetaByDomain
}

// ----------------------------------------------------------------------------
// HOOKS
// ----------------------------------------------------------------------------

/**
 * Reactive list of templates for the current filter set, hydrated from the
 * server-fetched first page so the grid is visible on first paint.
 *
 * The grid is intentionally type-agnostic — designs are untyped, so
 * automation-used and campaign-used rows surface together. See the page
 * doc + `tests/templates-gallery-mixes-types.test.ts`.
 */
function useGridEmails({
  initialEmails,
  isSpecialType,
  shouldSkipLiveQuery,
  currentType,
  currentBrand,
  currentDesigner,
  seed,
}: {
  initialEmails: Array<TemplateSnapshot>
  isSpecialType: boolean
  shouldSkipLiveQuery: boolean
  currentType?: string
  currentBrand?: string
  currentDesigner?: string
  seed?: string
}) {
  const queryArgs =
    isSpecialType || shouldSkipLiveQuery
      ? 'skip'
      : {
          ...templateTypeFilter(currentType),
          brandDomain: currentBrand,
          designerWebsite: currentDesigner,
          seed,
        }

  const { results, status, loadMore } = usePaginatedQuery(
    api.templates.queries.getPublicTemplatesPage,
    queryArgs,
    { initialNumItems: PAGE_SIZE }
  )
  const isHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydratedClientSnapshot,
    getHydratedServerSnapshot
  )

  // Show SSR data immediately; once the live subscription has its first page,
  // it becomes the source of truth (handles real-time updates + pagination).
  // Dedupe by opaque public identity defensively: a double-insert race can leave TWO
  // `version:'latest'` rows for one emailId (observed in prod-shaped
  // data), and duplicate keys break React reconciliation AND mount
  // duplicate `<ViewTransition>` names. First occurrence wins.
  const allEmails = useMemo<Array<TemplateSnapshot>>(() => {
    const source = !isHydrated || results.length === 0 ? initialEmails : results
    const seen = new Set<string>()
    return source.filter((snapshot) => {
      const publicReference = publicTemplateReferenceOf(snapshot)
      if (
        isReviewedTemplateAlias(publicReference) ||
        seen.has(publicReference)
      ) {
        return false
      }
      seen.add(publicReference)
      return true
    })
  }, [initialEmails, isHydrated, results])
  const hydrationSafeStatus = isHydrated ? status : 'LoadingFirstPage'

  const requestNextPage = useCallback(() => {
    if (status === 'CanLoadMore') {
      loadMore(PAGE_SIZE)
    }
  }, [status, loadMore])

  return {
    allEmails,
    status: hydrationSafeStatus,
    requestNextPage,
  }
}

function useEmailRoutePrefetch(
  emails: Array<TemplateSnapshot>,
  basePath: string
) {
  const router = useRouter()
  const prefetched = useRef(new Set<string>())

  useEffect(() => {
    // The detail page's authored-design preview is a split chunk: fetch it
    // with the routes so the click renders the preview, not its frame.
    preloadEmailJsxPreview().catch(() => {
      // The detail page's own render retries the import.
    })
    for (const email of emails) {
      const route = templateDetailHref(email, basePath)
      if (!prefetched.current.has(route)) {
        prefetched.current.add(route)
        // Warm the shared detail shell; the card's link prepares its
        // URL-specific content on pointer or keyboard intent.
        router.prefetch(route)
      }
    }
  }, [emails, router, basePath])
}

// ----------------------------------------------------------------------------
// COMPONENT
// ----------------------------------------------------------------------------

export function EmailGridClient({
  initialEmails,
  currentType,
  currentBrand,
  currentDesigner,
  userId,
  seed,
  sentSeed,
  brandMeta,
}: EmailGridClientProps) {
  const t = useTranslations('templates')
  const basePath = useTemplatesBasePath()
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [searchQuery] = useTemplateSearchQuery()
  const isSemanticSearch = searchQuery.trim().length > 0
  // `?type=` names a chip's group or a category; the search takes either.
  const searchFilter =
    currentType && !USER_COLLECTION_TEMPLATE_TYPES.has(currentType)
      ? templateTypeFilter(currentType)
      : {}
  const search = usePublicTemplateSearch({
    query: searchQuery,
    limit: TEMPLATE_GALLERY_SEARCH_LIMIT,
    ...searchFilter,
    brandDomain: currentBrand,
  })
  const searchEmails = useMemo(
    () => search.hits.map(toTemplateSnapshot),
    [search.hits]
  )

  const isForwardedSelected = currentType === FORWARDED_TYPE
  const isSentSelected = currentType === SENT_TYPE
  const isSpecialType = isForwardedSelected || isSentSelected

  const { allEmails, status, requestNextPage } = useGridEmails({
    initialEmails,
    isSpecialType,
    shouldSkipLiveQuery: isSemanticSearch,
    currentType,
    currentBrand,
    currentDesigner,
    seed,
  })

  usePageContentReadiness(
    ['web_templates', 'web_browse_templates'],
    resolvePageReadinessState({
      unavailable: isSemanticSearch && search.isDisabled,
      failed: Boolean(isSemanticSearch && search.error),
      loading: isSemanticSearch && search.isSearching,
      empty: (isSemanticSearch ? searchEmails : allEmails).length === 0,
    }),
    { enabled: !isSpecialType || isSemanticSearch }
  )
  const displayEmails = isSemanticSearch ? searchEmails : allEmails

  useInfiniteScrollSentinel(
    sentinelRef,
    !isSpecialType && !isSemanticSearch && status === 'CanLoadMore',
    requestNextPage
  )

  useEmailRoutePrefetch(displayEmails, basePath)

  const handleClearFilters = useCallback(() => {
    startTransition(() => {
      router.push(
        buildTemplatesUrl({ type: undefined, brand: undefined, basePath })
      )
    })
  }, [router, basePath])

  const handleClearSearch = useCallback(() => {
    startTransition(() => {
      router.push(
        buildTemplatesUrl({
          type: currentType,
          brand: currentBrand,
          designer: currentDesigner,
          basePath,
        })
      )
    })
  }, [basePath, currentBrand, currentDesigner, currentType, router])

  if (isSentSelected && !isSemanticSearch) {
    return <SentEmailGrid seed={sentSeed} />
  }

  if (isForwardedSelected && userId && !isSemanticSearch) {
    return <ForwardedEmailGrid initialEmails={initialEmails} userId={userId} />
  }

  if (isSemanticSearch && search.isDisabled) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-center text-muted-foreground">
          {t('grid.searchUnavailable')}
        </p>
      </div>
    )
  }

  if (isSemanticSearch && search.error) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-center text-muted-foreground">{search.error}</p>
      </div>
    )
  }

  if (isSemanticSearch && search.isSearching && searchEmails.length === 0) {
    return (
      <div className={TEMPLATE_GRID_CLASSES}>
        {Array.from({ length: 8 }, (_, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: static search-loading placeholders
            key={`search-skel-${index}`}
            className="aspect-[9/16] animate-pulse rounded-lg bg-muted/40"
          />
        ))}
      </div>
    )
  }

  if (isSemanticSearch && !search.isSearching && searchEmails.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-4 text-muted-foreground">
            {t('grid.noSearchMatches')}
          </p>
          <Button variant="outline" onClick={handleClearSearch}>
            {t('search.clearSearch')}
          </Button>
        </div>
      </div>
    )
  }

  if (!isSpecialType && !isSemanticSearch && initialEmails.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-4 text-muted-foreground">
            {t('grid.noFilterMatches')}
          </p>
          <Button variant="outline" onClick={handleClearFilters}>
            {t('grid.clearFilters')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col transition-opacity', {
        'pointer-events-none opacity-60': isPending,
      })}
    >
      <div className={TEMPLATE_GRID_CLASSES}>
        {displayEmails.map((snapshot, index) => {
          return (
            // Staggered blur-fade on first paint AND as rows scroll into
            // view (`inView` fires once per card). The delay cycles
            // (index % 6) so infinite-scroll pages never accumulate
            // multi-second waits deep in the list.
            <BlurFade
              key={publicTemplateReferenceOf(snapshot)}
              inView
              delay={(index % 6) * 0.06}
            >
              <EmailPreviewCard
                snapshot={snapshot}
                brandMeta={brandMeta}
                href={templateDetailHref(snapshot, basePath)}
              />
            </BlurFade>
          )
        })}
      </div>

      {!isSemanticSearch &&
      (status === 'CanLoadMore' || status === 'LoadingMore') ? (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center py-8"
        >
          {status === 'LoadingMore' ? (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
